import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL, apiFetch } from '../config/api';
import { getIconSVG } from '../utils/icons';
import { getCachedOpsConfig, loadOpsConfig } from '../config/opsConfig';
import { batteryVerdict, estimateDelivery } from '../utils/deliveryEstimate';
import { SECTIONS } from '../config/constants';

/**
 * Deliveries Page — saved delivery locations (address book) + the parcel queue.
 *
 * Queue tab:     add a parcel against a saved location, order the stops, check
 *                the run against the drone's battery, then dispatch them as one
 *                delivery mission.
 * Locations tab: the address book those parcels point at.
 *
 * Dispatch reuses the existing delivery-mission endpoint (POST
 * /drones/:id/delivery with jobIds), so there is no second mission generator:
 * coordinates are resolved server-side from the saved location records.
 *
 * Dispatch uploads; it does NOT launch. Starting a flight goes through the
 * Missions page because that is where the preflight checklist lives, and a
 * one-click take-off from a queue screen would route around it.
 */

const MAX_STOPS = 10;        // backend caps a delivery mission at 10 stops
const LIVE_POLL_MS = 6000;   // refresh cadence while parcels are actually flying
const FINISHED_PAGE = 8;     // rows of history shown before "show all"

const STATUS_META = {
  requested: { label: 'Requested', color: '#a78bfa' },
  pending: { label: 'Pending', color: 'var(--textMuted)' },
  assigned: { label: 'Assigned', color: '#fbbf24' },
  in_flight: { label: 'In flight', color: 'var(--accent)' },
  landed: { label: 'Landed · awaiting pickup', color: '#fb923c' },
  delivered: { label: 'Delivered', color: '#34d399' },
  failed: { label: 'Failed', color: '#f87171' },
};

async function call(url, options) {
  const res = await apiFetch(url, options);
  let data = {};
  try { data = await res.json(); } catch { /* non-JSON error body */ }
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

const Icon = ({ name, size = 16, color = 'currentColor' }) => (
  <span
    style={{ display: 'inline-flex', alignItems: 'center' }}
    dangerouslySetInnerHTML={{ __html: getIconSVG(name, size, color) }}
  />
);

const PAGE_CSS = `
.dlv{height:100%;overflow-y:auto;background:var(--bgPrimary);color:#ffffff;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;}
.dlv-inner{max-width:100%;margin:0;padding:24px 32px 64px;box-sizing:border-box;}
.dlv h1{font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:24px;font-weight:700;margin:0;color:#ffffff;letter-spacing:-0.01em;}
.dlv-sub{font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:13.5px;font-weight:400;color:#ffffff;opacity:0.92;margin:6px 0 0;line-height:1.45;}
.dlv-layout{display:flex;gap:24px;align-items:flex-start;margin-top:22px;}
.dlv-main{flex:1;min-width:0;}
.dlv-sidebar{width:160px;flex-shrink:0;position:sticky;top:24px;}
.dlv-stats-vertical{display:flex;flex-direction:column;gap:10px;}
.dlv-stat{padding:14px 16px;border:1px solid var(--borderSecondary);border-radius:10px;background:var(--bgSecondary);text-align:center;display:flex;flex-direction:column;align-items:center;justify-content:center;transition:all .18s ease;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;user-select:none;}
.dlv-stat.clickable{cursor:pointer;}
.dlv-stat.clickable:hover{border-color:rgba(34,211,238,.65);background:rgba(34,211,238,.08);transform:translateY(-2px);box-shadow:0 4px 12px rgba(0,0,0,.25);}
.dlv-stat.active{border-color:var(--accent);background:rgba(34,211,238,.12);box-shadow:0 0 12px rgba(34,211,238,.22);}
.dlv-stat.active .k{color:var(--accent) !important;opacity:1 !important;}
.dlv-stat .n{font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:24px;font-weight:700;line-height:1;color:#ffffff;}
.dlv-stat .k{display:block;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:10px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#ffffff;opacity:0.9;margin-top:6px;white-space:nowrap;}
.dlv-stat.live{border-color:rgba(34,211,238,.55);}
.dlv-tabs{display:inline-flex;gap:4px;background:var(--bgSecondary);border:1px solid var(--borderSecondary);padding:4px;border-radius:8px;}
.dlv-tab{background:none;border:none;padding:7px 18px;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:12px;font-weight:600;letter-spacing:.03em;text-transform:uppercase;color:#ffffff;opacity:0.8;cursor:pointer;border-radius:6px;transition:all .15s ease;}
.dlv-tab:hover{color:#ffffff;opacity:1;}
.dlv-tab.on{color:#06212b;background:var(--accent);font-weight:700;opacity:1;box-shadow:0 1px 4px rgba(0,0,0,.25);}

@media(max-width:1100px){
  .dlv-layout{flex-direction:column-reverse;}
  .dlv-sidebar{width:100%;position:static;}
  .dlv-stats-vertical{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));}
}
.dlv-top-grid{display:grid;grid-template-columns:3fr 7fr;gap:14px;margin-bottom:14px;align-items:stretch;}
@media(max-width:960px){.dlv-top-grid{grid-template-columns:1fr;}}
.dlv-half-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;align-items:stretch;}
@media(max-width:960px){.dlv-half-grid{grid-template-columns:1fr;}}
.dlv-card{border:1px solid var(--borderSecondary);border-radius:12px;background:var(--bgSecondary);padding:18px 20px;margin-bottom:14px;}
.dlv-card-slim{border:1px solid var(--borderSecondary);border-radius:10px;background:var(--bgSecondary);padding:11px 18px;margin-bottom:14px;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;}
.dlv-card-slim-left{display:flex;align-items:center;gap:8px;}
.dlv-card-slim-title{font-size:12.5px;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#ffffff;}
.dlv-card-slim-hint{font-size:12px;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;color:color-mix(in srgb, var(--textPrimary) 78%, transparent);}
.dlv-card.focus{border-color:rgba(34,211,238,.5);box-shadow:0 0 0 1px rgba(34,211,238,.14);}
.dlv-card h2{font-size:13px;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#ffffff;margin:0 0 14px;display:flex;align-items:center;gap:8px;}
.dlv-card h2 .count{color:var(--accent);font-weight:700;}
.dlv-grid{display:grid;gap:12px;}
.dlv-lbl{display:block;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:#ffffff;margin-bottom:6px;}
.dlv-in{width:100%;box-sizing:border-box;height:36px;padding:0 12px;border:1px solid var(--borderSecondary);border-radius:6px;background:var(--bgPrimary);color:#ffffff;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:13px;outline:none;}
.dlv-in:focus{border-color:var(--accent);box-shadow:0 0 0 2px rgba(34,211,238,.18);}
.dlv-in.bad{border-color:#f87171;}
.dlv-hint{font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:11px;color:#ffffff;opacity:0.85;margin-top:4px;display:block;}
.dlv-hint.bad{color:#f87171;opacity:1;}
.dlv-btn{height:34px;padding:0 16px;border-radius:6px;border:1px solid var(--borderSecondary);background:var(--bgTertiary);color:#ffffff;cursor:pointer;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:11.5px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;display:inline-flex;align-items:center;gap:7px;transition:all .15s ease;}
.dlv-btn:hover:not(:disabled){border-color:var(--accent);color:var(--accent);}
.dlv-btn:disabled{opacity:.45;cursor:not-allowed;}
.dlv-btn.primary{background:var(--accent);border-color:var(--accent);color:#06212b;font-weight:700;}
.dlv-btn.primary:hover:not(:disabled){filter:brightness(1.1);color:#06212b;}
.dlv-btn.link{background:none;border:none;padding:0 6px;height:26px;color:#ffffff;opacity:0.85;}
.dlv-btn.link:hover:not(:disabled){color:var(--accent);opacity:1;}
.dlv-btn.danger:hover:not(:disabled){border-color:#f87171;color:#f87171;}
.dlv-btn.tiny{height:22px;width:22px;padding:0;justify-content:center;border-radius:5px;font-size:12px;}
.dlv-scroll{overflow-x:auto;}
.dlv-tbl{width:100%;border-collapse:collapse;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:13px;min-width:520px;}
.dlv-tbl th{text-align:left;font-size:11px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:#ffffff;padding:10px 12px;border-bottom:1px solid var(--borderSecondary);white-space:nowrap;}
.dlv-tbl td{padding:10px 12px;border-bottom:1px solid var(--borderSecondary);vertical-align:middle;color:#ffffff;}
.dlv-tbl tbody tr:hover{background:rgba(34,211,238,.06);}
.dlv-tbl tbody tr.sel{background:rgba(34,211,238,.11);}
.dlv-ord{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:var(--accent);color:#06212b;font-size:11px;font-weight:700;}
.dlv-chip{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;}
.dlv-chip .dot{width:7px;height:7px;border-radius:50%;}
.dlv-empty{padding:24px 10px;text-align:center;color:#ffffff;opacity:0.9;font-size:13px;}
.dlv-empty b{display:block;color:#ffffff;font-weight:600;margin-bottom:4px;font-size:14.5px;opacity:1;}
.dlv-msg{padding:10px 14px;border-radius:8px;font-size:13px;margin-bottom:14px;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;}
.dlv-msg.err{background:rgba(248,113,113,.13);border:1px solid rgba(248,113,113,.4);color:#f87171;}
.dlv-msg.ok{background:rgba(52,211,153,.13);border:1px solid rgba(52,211,153,.4);color:#34d399;}
.dlv-bar{display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding-top:14px;margin-top:4px;border-top:1px solid var(--borderSecondary);}
.dlv-est{font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:12.5px;color:#ffffff;opacity:0.92;letter-spacing:.01em;line-height:1.5;}
.dlv-est b{color:#ffffff;font-weight:700;opacity:1;}
.dlv-facts{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:10px;margin-bottom:14px;}
.dlv-fact{padding:10px 12px;border-radius:8px;background:var(--bgPrimary);border:1px solid var(--borderSecondary);}
.dlv-fact .v{font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:15px;font-weight:700;color:#ffffff;}
.dlv-fact .k{display:block;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:#ffffff;opacity:0.85;margin-top:5px;}
.dlv-warn{padding:10px 12px;border-radius:8px;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:12px;margin-bottom:12px;display:flex;gap:8px;align-items:flex-start;line-height:1.45;}
.dlv-warn.amber{background:rgba(251,191,36,.12);border:1px solid rgba(251,191,36,.42);color:#fbbf24;}
.dlv-warn.red{background:rgba(248,113,113,.12);border:1px solid rgba(248,113,113,.42);color:#f87171;}
.dlv-warn.dim{background:var(--bgPrimary);border:1px solid var(--borderSecondary);color:#ffffff;opacity:0.9;}
.dlv-live{display:inline-flex;align-items:center;gap:6px;font-family:'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;font-size:11px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--accent);}
.dlv-live .pulse{width:7px;height:7px;border-radius:50%;background:var(--accent);animation:dlv-pulse 1.6s ease-in-out infinite;}
@keyframes dlv-pulse{0%,100%{opacity:1}50%{opacity:.25}}
@media (max-width:768px){
  .dlv-inner{padding:16px 16px 24px;}
  .dlv-stats{grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;}
  .dlv-stat{padding:10px 8px;border-radius:12px;}
  .dlv-stat .n{font-size:18px;}
  .dlv-stat .k{font-size:9px;letter-spacing:.05em;margin-top:5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .dlv-card{padding:16px;}
  .dlv-grid{grid-template-columns:1fr !important;}
  .dlv-in{height:40px;}
  .dlv-btn{height:40px;}
}
`;

function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status, color: 'var(--textMuted)' };
  return (
    <span className="dlv-chip" style={{ color: meta.color }}>
      <span className="dot" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

function Stat({ n, k, live = false, onClick, active = false }) {
  return (
    <div
      className={`dlv-stat${live ? ' live' : ''}${onClick ? ' clickable' : ''}${active ? ' active' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
      title={onClick ? `Switch to ${k} view` : undefined}
    >
      <span className="n">{n}</span>
      <span className="k">{k}</span>
    </div>
  );
}

function Fact({ v, k }) {
  return <div className="dlv-fact"><span className="v">{v}</span><span className="k">{k}</span></div>;
}

// "21.1458, 79.0882" — one paste fills both boxes.
//
// Read as lat,lon, the near-universal convention. The ONLY case that can be
// detected and corrected is a first value outside +/-90, which cannot be a
// latitude at all — so a lon/lat pair like "179.5, 21.1" is swapped. A pair
// where both numbers are valid latitudes (79, 21 — Nagpur written either way)
// is genuinely ambiguous and is taken at face value; the operator sees the
// parsed values land in the labelled boxes and can fix them there.
export function parseCoordPair(text) {
  const m = String(text || '').trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  let lat = parseFloat(m[1]);
  let lon = parseFloat(m[2]);
  if (Math.abs(lat) > 90 && Math.abs(lon) <= 90) [lat, lon] = [lon, lat];
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

function DeliveriesPage({ drones = [], dronesTelemetry = {}, selectedDroneId, onDroneSelect, onNavigate }) {
  const [tab, setTab] = useState('requests');
  const [requestSubTab, setRequestSubTab] = useState('requests'); // 'requests' | 'rerequests'
  const [locSubTab, setLocSubTab] = useState('locations'); // 'locations' | 'centers'
  const [locations, setLocations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [links, setLinks] = useState([]);
  const [centers, setCenters] = useState([]);
  const [linkForm, setLinkForm] = useState({ label: '', centerId: '', contact: '', droneId: '', expiresInDays: '7' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [copiedLinkId, setCopiedLinkId] = useState(null);
  const [copiedJobId, setCopiedJobId] = useState(null);
  const [openMenuLinkId, setOpenMenuLinkId] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [cfg, setCfg] = useState(() => getCachedOpsConfig());
  const [lastDispatch, setLastDispatch] = useState(null);
  // How this run drops its parcels: 'winch' (hover + rope) or 'touch' (set down, release, fly on).
  const [dropMethod, setDropMethod] = useState('winch');
  const [locFilter, setLocFilter] = useState('');
  const [centerFilter, setCenterFilter] = useState('');
  const [showAllFinished, setShowAllFinished] = useState(false);

  // New-parcel form
  const [jobForm, setJobForm] = useState({ locationId: '', recipient: '', parcelWeightKg: '', note: '', type: 'ecom' });

  // Location form — `editingId` null means "adding"
  const [locForm, setLocForm] = useState({ name: '', lat: '', lon: '', address: '', landingPad: false });
  const [editingId, setEditingId] = useState(null);

  // Delivery Center form — `editingCenterId` null means "adding"
  const [centerForm, setCenterForm] = useState({ name: '', centerId: '', code: '', lat: '', lon: '', serviceRadiusKm: '6', address: '', notes: '' });
  const [editingCenterId, setEditingCenterId] = useState(null);

  const reload = useCallback(async ({ quiet = false } = {}) => {
    try {
      const [locRes, jobRes, linkRes, centerRes] = await Promise.all([
        call(`${API_URL}/delivery/locations`),
        call(`${API_URL}/delivery/jobs`),
        call(`${API_URL}/delivery/links`),
        call(`${API_URL}/delivery/centers`),
      ]);
      setLocations(locRes.locations || []);
      setJobs(jobRes.jobs || []);
      setLinks(linkRes.links || []);
      setCenters(centerRes.centers || []);
      // Deliberately does not clear `error`: reload runs after a failed action
      // and must not wipe the message explaining what failed.
    } catch (err) {
      // A background poll that blips must not replace what the operator is
      // currently reading; only an action-driven reload surfaces its failure.
      if (!quiet) setError(err.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  // Jobs, locations and centres changed on another screen (or by the dispatch
  // loop): refresh quietly, as the background poll does.
  useEffect(() => {
    const onChange = (e) => {
      if (['deliveryJob', 'deliveryLocation', 'deliveryCenter'].includes(e.detail && e.detail.entity)) reload({ quiet: true });
    };
    window.addEventListener('utms:entity-changed', onChange);
    return () => window.removeEventListener('utms:entity-changed', onChange);
  }, [reload]);
  useEffect(() => { loadOpsConfig().then(setCfg).catch(() => {}); }, []);

  // Close 3-dots dropdown menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.dlv-menu-wrap')) {
        setOpenMenuLinkId(null);
      }
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  const byStatus = useMemo(() => {
    const groups = { requested: [], pending: [], assigned: [], in_flight: [], landed: [], delivered: [], failed: [] };
    jobs.forEach((job) => {
      // An unrecognised status goes to `failed`, never to `pending`: whatever it
      // is, it is not something an operator should be able to select and fly.
      // StatusChip still renders the raw value, so it stays visible.
      (groups[job.status] || groups.failed).push(job);
    });
    return groups;
  }, [jobs]);

  const customerRequests = useMemo(() => {
    return byStatus.requested.filter((j) => !j.isReRequest && j.origin !== 'rerequest' && j.status !== 'rerequested');
  }, [byStatus.requested]);

  const customerReRequests = useMemo(() => {
    return jobs.filter((j) => (j.isReRequest || j.origin === 'rerequest' || j.status === 'rerequested') && j.status !== 'delivered' && j.status !== 'failed');
  }, [jobs]);

  const currentRequestsList = requestSubTab === 'requests' ? customerRequests : customerReRequests;

  // Poll only while something is actually in the air. An idle queue changes when
  // the operator changes it, and a timer against an empty board is pure load.
  // Medical drones still at (or not yet back from) their pad: landed and
  // waiting for the recipient, or received and waiting for the return flight.
  const medicalParked = useMemo(
    () => jobs.filter((j) => j.type === 'medical' && (j.status === 'landed' || j.status === 'delivered') && !j.returnedAt),
    [jobs]
  );
  const liveCount = byStatus.assigned.length + byStatus.in_flight.length + medicalParked.length;
  const parkedForSelected = medicalParked.find((j) => selectedDroneId && j.droneId === selectedDroneId) || null;
  // Waiting clock for landed medical drones. Alert only: past the configured
  // minutes the row turns red; the drone never leaves on its own.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const anyLanded = medicalParked.some((j) => j.status === 'landed');
  useEffect(() => {
    if (!anyLanded) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(id);
  }, [anyLanded]);
  const pickupTimeoutMin = Number(cfg?.delivery?.pickupTimeoutMin) > 0 ? Number(cfg.delivery.pickupTimeoutMin) : 15;
  const waitedMin = (job) => (job.landedAt ? Math.max(0, Math.floor((nowMs - new Date(job.landedAt).getTime()) / 60000)) : null);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (liveCount === 0) return undefined;
    const id = setInterval(() => { reloadRef.current({ quiet: true }); }, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [liveCount]);

  // Drop selections that are no longer pending (dispatched elsewhere, deleted…)
  useEffect(() => {
    setSelectedJobIds((prev) => prev.filter(
      (id) => jobs.some((j) => j._id === id && j.status === 'pending')
    ));
  }, [jobs]);

  // Always reloads, including on failure: a request can fail *after* the server
  // changed something (mission created, upload rejected), and a stale queue is
  // the one thing an operator must not be shown.
  const run = async (fn, successMessage) => {
    setBusy(true);
    setError('');
    setOkMsg('');
    try {
      await fn();
      if (successMessage) setOkMsg(successMessage);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      await reload();
      setBusy(false);
    }
  };

  // Selection order IS flight order — preserve it, never sort by anything else.
  const selectedJobs = useMemo(
    () => selectedJobIds.map((id) => jobs.find((j) => j._id === id)).filter(Boolean),
    [selectedJobIds, jobs]
  );

  const selectedHasMedical = selectedJobs.some((j) => j.type === 'medical');
  const dispatchDrone = drones.find((d) => d.id === selectedDroneId) || null;
  const droneConnected = dispatchDrone?.status === 'connected';
  // The backend refuses a method the drone has no hardware for; say so before the click.
  const hardwareMissing = !!dispatchDrone && !dispatchDrone.isSimulated
    && !dispatchDrone.payload?.[dropMethod === 'touch' ? 'hook' : 'winch'];
  const dronePos = dronesTelemetry[selectedDroneId];

  const payloadKg = useMemo(
    () => selectedJobs.reduce((sum, j) => sum + (Number(j.parcelWeightKg) || 0), 0),
    [selectedJobs]
  );

  // Round trip from where the drone actually is, through each stop in the chosen
  // order, and back home.
  const estimate = useMemo(() => {
    // Number.isFinite, not truthiness: a drone sitting on the equator or the
    // prime meridian reports a legitimate 0 that `&&` would throw away.
    const start = dronePos && Number.isFinite(Number(dronePos.lat)) && Number.isFinite(Number(dronePos.lon))
      ? { lat: Number(dronePos.lat), lon: Number(dronePos.lon) }
      : null;
    const stops = selectedJobs.filter((j) => j.location).map((j) => ({ lat: j.location.lat, lon: j.location.lon }));
    return estimateDelivery({
      start, stops, navSpeed: cfg?.delivery?.navSpeed, payloadKg,
      winch: {
        ropeLength: cfg?.delivery?.ropeLength,
        winchRate: cfg?.delivery?.winchRate,
        settleHold: cfg?.dropTiming?.settleHoldSeconds,
        postDropHold: cfg?.dropTiming?.postDropHoldSeconds,
      },
      dropMethod,
      dropHeight: cfg?.delivery?.dropHeight,
    });
  }, [dronePos, selectedJobs, cfg, payloadKg, dropMethod]);

  const battery = useMemo(
    () => batteryVerdict(estimate, dronePos?.battery_remaining),
    [estimate, dronePos]
  );

  const toggleJob = (id) => {
    setSelectedJobIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_STOPS) return prev;
      return [...prev, id];
    });
  };

  const moveStop = (id, dir) => {
    setSelectedJobIds((prev) => {
      const i = prev.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const addJob = () => run(async () => {
    if (!jobForm.locationId) throw new Error('Pick a delivery location');
    await call(`${API_URL}/delivery/jobs`, { method: 'POST', body: JSON.stringify(jobForm) });
    setJobForm({ locationId: '', recipient: '', parcelWeightKg: '', note: '', type: 'ecom' });
  }, 'Parcel queued');

  const deleteJob = (id) => run(
    () => call(`${API_URL}/delivery/jobs/${id}`, { method: 'DELETE' }),
    'Delivery removed'
  );

  // A customer request is a drop point no operator has looked at yet. Approving
  // moves it into the normal pending queue; rejecting closes it out as failed.
  const approveJob = (id) => run(
    () => call(`${API_URL}/delivery/jobs/${id}/approve`, { method: 'PATCH' }),
    'Request approved — the parcel is now in the pending queue'
  );

  const rejectJob = (job) => {
    if (!window.confirm(`Reject the request from ${job.recipient || 'this customer'}?`)) return;
    run(() => call(`${API_URL}/delivery/jobs/${job._id}/reject`, { method: 'PATCH' }), 'Request rejected');
  };

  const acceptReRequest = (id) => run(
    () => call(`${API_URL}/delivery/jobs/${id}/rerequest/accept`, { method: 'PATCH' }),
    'Re-request accepted — copy link to send to customer'
  );

  const rejectReRequest = (job) => {
    if (!window.confirm(`Reject this re-request from ${job.label || job.recipient || 'this customer'}?`)) return;
    run(() => call(`${API_URL}/delivery/jobs/${job._id}/rerequest/reject`, { method: 'PATCH' }), 'Re-request rejected');
  };

  const copyReRequestLink = async (job) => {
    const token = job.linkToken || (links.length > 0 ? links[0].token : '');
    const url = `${window.location.origin}${window.location.pathname}?drop=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedJobId(job._id);
      setTimeout(() => setCopiedJobId(null), 1800);
      setOkMsg('Link copied to clipboard');
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const createLink = () => run(async () => {
    await call(`${API_URL}/delivery/links`, { method: 'POST', body: JSON.stringify(linkForm) });
    setLinkForm({ label: '', centerId: '', contact: '', droneId: '', expiresInDays: '7' });
  }, 'Share link created — ready to send');

  const revokeLink = (link) => {
    if (!window.confirm(`Revoke "${link.label || 'this link'}"? Anyone holding the URL loses access immediately.`)) return;
    run(() => call(`${API_URL}/delivery/links/${link._id}/revoke`, { method: 'PATCH' }), 'Share link revoked');
  };

  const deleteLink = (link) => {
    if (!window.confirm(`Delete "${link.label || 'this link'}"?`)) return;
    run(() => call(`${API_URL}/delivery/links/${link._id}`, { method: 'DELETE' }), 'Share link deleted');
  };

  const clearInactiveLinks = () => run(
    () => call(`${API_URL}/delivery/links/clear-inactive`, { method: 'POST' }),
    'Inactive links cleared'
  );

  const linkUrl = (link) => `${window.location.origin}${window.location.pathname}?drop=${link.token}`;

  const copyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(linkUrl(link));
      setCopiedLinkId(link._id);
      setTimeout(() => setCopiedLinkId(null), 1800);
      setOkMsg('Link copied to clipboard');
    } catch {
      // Clipboard is blocked on insecure origins — show the URL so it can be
      // selected by hand rather than failing silently.
      window.prompt('Copy this link:', linkUrl(link));
    }
  };

  const requeueJob = (id) => run(
    () => call(`${API_URL}/delivery/jobs/${id}/requeue`, { method: 'PATCH' }),
    'Delivery back in the pending queue'
  );

  const completeJob = (id) => run(
    () => call(`${API_URL}/delivery/jobs/${id}/complete`, { method: 'PATCH' }),
    'Delivery marked as delivered'
  );

  // ── Medical: received → prepare return → (Start in Missions) → back ──
  const recipientUrl = (job) => `${window.location.origin}${window.location.pathname}?receive=${job.trackToken}`;
  const copyRecipientLink = async (job) => {
    try {
      await navigator.clipboard.writeText(recipientUrl(job));
      setOkMsg('Recipient link copied — send it to whoever collects the parcel');
    } catch {
      window.prompt('Copy this link for the recipient:', recipientUrl(job));
    }
  };
  const markReceived = (job) => {
    if (!window.confirm('Confirm the parcel was taken off the drone and nobody is near it?')) return;
    run(() => call(`${API_URL}/delivery/jobs/${job._id}/received`, { method: 'PATCH' }), 'Parcel marked received — you can prepare the return flight');
  };
  // One click: confirm received (if still landed) → upload the return flight →
  // start it through the normal start route (preflight + airspace claim run
  // there; a failed check stops here with its message). The operator's click is
  // the human gate — the confirm dialog says what it vouches for.
  const sendDroneBack = (job) => {
    const msg = job.status === 'landed'
      ? 'Send the drone back now?\n\nThis also confirms the parcel was taken off the drone and nobody is near it.'
      : 'Send the drone back now? Nobody may be near the drone.';
    if (!window.confirm(msg)) return;
    run(async () => {
      if (job.status === 'landed') {
        await call(`${API_URL}/delivery/jobs/${job._id}/received`, { method: 'PATCH' });
      }
      const prep = await call(`${API_URL}/drones/${job.droneId}/medical-return`, { method: 'POST', body: JSON.stringify({ jobId: job._id }) });
      const missionId = prep.mission && (prep.mission.id || prep.mission.missionId);
      if (!missionId) throw new Error('Return flight was not created');
      setLastDispatch({ droneId: job.droneId, mission: prep.mission, stops: 1, deconfliction: prep.deconfliction });
      await call(`${API_URL}/drones/${job.droneId}/missions/${encodeURIComponent(missionId)}/start`, {
        method: 'POST', body: JSON.stringify({ skip_gps_checks: true }),
      });
    }, 'Drone is on its way back');
  };
  const markRecovered = (job) => {
    if (!window.confirm('Mark this drone as back at base? Only do this if it really is — it becomes free for new deliveries.')) return;
    run(() => call(`${API_URL}/delivery/jobs/${job._id}/recovered`, { method: 'PATCH' }), 'Drone marked recovered');
  };

  const reconcileStaleJobs = () => run(
    () => call(`${API_URL}/delivery/jobs/reconcile`, { method: 'POST' }),
    'Stale flights reconciled'
  );

  const dispatch = () => run(async () => {
    if (!selectedDroneId) throw new Error('Select a drone to dispatch to');
    if (selectedJobIds.length === 0) throw new Error('Select at least one pending delivery');
    const stops = selectedJobIds.length;
    const data = await call(`${API_URL}/drones/${selectedDroneId}/delivery`, {
      method: 'POST',
      body: JSON.stringify({ jobIds: selectedJobIds, dropMethod }),
    });
    setSelectedJobIds([]);
    if (!data.uploaded) {
      throw new Error(`Mission created but upload failed: ${data.uploadMessage || 'unknown error'}`);
    }
    setLastDispatch({ droneId: selectedDroneId, mission: data.mission, stops, deconfliction: data.deconfliction });
  }, 'Mission uploaded — start it from Missions, where preflight runs');

  // Client-side coordinate check so a typo fails here instead of after a round
  // trip. The server validates the same range again — this is convenience, not
  // the trust boundary.
  const latNum = parseFloat(locForm.lat);
  const lonNum = parseFloat(locForm.lon);
  const latBad = locForm.lat !== '' && (!Number.isFinite(latNum) || Math.abs(latNum) > 90);
  const lonBad = locForm.lon !== '' && (!Number.isFinite(lonNum) || Math.abs(lonNum) > 180);
  const locReady = locForm.name.trim() !== '' && locForm.lat !== '' && locForm.lon !== '' && !latBad && !lonBad;

  const pasteCoords = (e) => {
    const pair = parseCoordPair(e.clipboardData?.getData('text'));
    if (!pair) return;
    e.preventDefault();
    setLocForm((f) => ({ ...f, lat: String(pair.lat), lon: String(pair.lon) }));
  };

  const submitLocation = () => run(async () => {
    const body = JSON.stringify(locForm);
    if (editingId) {
      await call(`${API_URL}/delivery/locations/${editingId}`, { method: 'PUT', body });
    } else {
      await call(`${API_URL}/delivery/locations`, { method: 'POST', body });
    }
    setLocForm({ name: '', lat: '', lon: '', address: '', landingPad: false });
    setEditingId(null);
  }, editingId ? 'Location updated' : 'Location saved');

  const deleteLocation = (loc) => {
    // Destructive and not undoable, and the whole address book points at these —
    // confirm before it goes.
    if (!window.confirm(`Delete "${loc.name}"? Parcels already in flight keep the coordinates they were dispatched with.`)) return;
    run(() => call(`${API_URL}/delivery/locations/${loc._id}`, { method: 'DELETE' }), 'Location deleted');
  };

  const startEdit = (loc) => {
    setEditingId(loc._id);
    setLocForm({ name: loc.name, lat: String(loc.lat), lon: String(loc.lon), address: loc.address || '', landingPad: Boolean(loc.landingPad) });
    setTab('locations');
    setLocSubTab('locations');
  };

  const queueFor = (loc) => {
    setJobForm({ locationId: loc._id, recipient: '', parcelWeightKg: '', note: '', type: loc.landingPad ? 'medical' : 'ecom' });
    setTab('queue');
  };

  // Center coordinate validation & submit
  const centerLatNum = parseFloat(centerForm.lat);
  const centerLonNum = parseFloat(centerForm.lon);
  const centerRadiusNum = parseFloat(centerForm.serviceRadiusKm);
  const centerLatBad = centerForm.lat !== '' && (!Number.isFinite(centerLatNum) || Math.abs(centerLatNum) > 90);
  const centerLonBad = centerForm.lon !== '' && (!Number.isFinite(centerLonNum) || Math.abs(centerLonNum) > 180);
  const centerRadiusBad = centerForm.serviceRadiusKm !== '' && (!Number.isFinite(centerRadiusNum) || centerRadiusNum <= 0);
  const centerReady = centerForm.name.trim() !== '' && centerForm.lat !== '' && centerForm.lon !== '' && !centerLatBad && !centerLonBad && !centerRadiusBad;

  const pasteCenterCoords = (e) => {
    const pair = parseCoordPair(e.clipboardData?.getData('text'));
    if (!pair) return;
    e.preventDefault();
    setCenterForm((f) => ({ ...f, lat: String(pair.lat), lon: String(pair.lon) }));
  };

  const submitCenter = () => run(async () => {
    const payload = {
      name: centerForm.name.trim(),
      centerId: centerForm.centerId?.trim() || '',
      code: centerForm.code?.trim() || '',
      lat: centerLatNum,
      lon: centerLonNum,
      serviceRadiusMeters: Math.round((centerRadiusNum || 6) * 1000),
      address: centerForm.address.trim(),
      notes: centerForm.notes?.trim() || '',
    };
    if (editingCenterId) {
      await call(`${API_URL}/delivery/centers/${editingCenterId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } else {
      await call(`${API_URL}/delivery/centers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
    setCenterForm({ name: '', centerId: '', code: '', lat: '', lon: '', serviceRadiusKm: '6', address: '', notes: '' });
    setEditingCenterId(null);
  }, editingCenterId ? 'Delivery center updated' : 'Delivery center saved');

  const startEditCenter = (c) => {
    setEditingCenterId(c._id);
    setCenterForm({
      name: c.name,
      centerId: c.centerId || '',
      code: c.code || '',
      lat: String(c.lat),
      lon: String(c.lon),
      serviceRadiusKm: String(((c.serviceRadiusMeters || 6000) / 1000).toFixed(1)),
      address: c.address || '',
      notes: c.notes || '',
    });
    setTab('locations');
    setLocSubTab('centers');
  };

  const deleteCenter = (c) => {
    if (!window.confirm(`Delete center "${c.name}"? Customer links tied to this center will fall back to default depot.`)) return;
    run(() => call(`${API_URL}/delivery/centers/${c._id}`, { method: 'DELETE' }), 'Delivery center deleted');
  };

  const createLinkForCenter = (c) => {
    setLinkForm((prev) => ({
      ...prev,
      centerId: c._id,
      label: prev.label || `${c.name} Drop Link`,
    }));
    setTab('requests');
  };

  const locationName = (job) => (job.location ? job.location.name : '(location deleted)');

  const visibleLocations = useMemo(() => {
    const q = locFilter.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter((l) =>
      l.name.toLowerCase().includes(q) || String(l.address || '').toLowerCase().includes(q));
  }, [locations, locFilter]);

  const visibleCenters = useMemo(() => {
    const q = centerFilter.trim().toLowerCase();
    if (!q) return centers;
    return centers.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      String(c.centerId || '').toLowerCase().includes(q) ||
      String(c.code || '').toLowerCase().includes(q) ||
      String(c.address || '').toLowerCase().includes(q) ||
      String(c.notes || '').toLowerCase().includes(q)
    );
  }, [centers, centerFilter]);

  const finished = useMemo(
    () => [...byStatus.failed, ...byStatus.delivered],
    [byStatus.failed, byStatus.delivered]
  );
  const finishedShown = showAllFinished ? finished : finished.slice(0, FINISHED_PAGE);

  return (
    <div className="dlv">
      <style>{PAGE_CSS}</style>
      <div className="dlv-inner">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ color: '#ffffff', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: 24, fontWeight: 700, margin: 0 }}>
              Deliveries
            </h1>
            <p className="dlv-sub" style={{ color: '#ffffff', opacity: 1, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: 13.5, margin: '6px 0 0', fontWeight: 400 }}>
              Queue parcels against saved locations, then fly them as one mission.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {liveCount > 0 && (
              <span className="dlv-live" title={`Auto-refreshing every ${LIVE_POLL_MS / 1000}s while parcels are in the air`}>
                <span className="pulse" /> Live
              </span>
            )}
            <button className="dlv-btn" onClick={() => run(async () => {})} disabled={busy}>
              <Icon name="loading" size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="dlv-layout">
          <div className="dlv-main">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
              <div className="dlv-tabs">
                <button
                  className={`dlv-tab ${tab === 'requests' ? 'on' : ''}`}
                  onClick={() => setTab('requests')}
                >
                  Request
                </button>
                <button
                  className={`dlv-tab ${tab === 'queue' ? 'on' : ''}`}
                  onClick={() => setTab('queue')}
                >
                  Queue
                </button>
                <button
                  className={`dlv-tab ${tab === 'locations' ? 'on' : ''}`}
                  onClick={() => setTab('locations')}
                >
                  Location
                </button>
              </div>
            </div>

            {error && <div className="dlv-msg err">{error}</div>}
            {okMsg && <div className="dlv-msg ok">{okMsg}</div>}
            {parkedForSelected && (
              // The selected drone is still on a medical pad: say so up here, with
              // the way out, instead of only refusing the next dispatch.
              <div className="dlv-msg err" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: 220 }}>
                  <b>{(dispatchDrone && (dispatchDrone.name || dispatchDrone.id)) || parkedForSelected.droneId}</b> is on the medical pad at{' '}
                  <b>{locationName(parkedForSelected)}</b>
                  {parkedForSelected.status === 'landed' ? ' — waiting for the parcel to be taken.' : ' — parcel received.'}
                  {' '}Send it back before dispatching it again.
                </span>
                <button className="dlv-btn primary tiny" onClick={() => sendDroneBack(parkedForSelected)} disabled={busy}>
                  Send drone back
                </button>
                <button className="dlv-btn link" onClick={() => markRecovered(parkedForSelected)} disabled={busy} title="The drone is already back at base by other means">
                  Recovered
                </button>
              </div>
            )}

            {loading ? (
              <div className="dlv-empty">Loading…</div>
        ) : tab === 'queue' ? (
          <>
            {/* ── Manifest: what is about to fly, in the order it will fly ── */}
            {selectedJobs.length > 0 && (
              <div className="dlv-card focus">
                <h2>
                  <Icon name="takeoffAction" size={14} />
                  Ready to dispatch · <span className="count">{selectedJobs.length} {selectedJobs.length === 1 ? 'stop' : 'stops'}</span>
                </h2>

                <div className="dlv-facts">
                  <Fact v={estimate ? `${estimate.km.toFixed(2)} km` : '—'} k="Round trip" />
                  <Fact v={estimate ? `${Math.ceil(estimate.minutes)} min` : '—'} k="Flight time" />
                  <Fact v={payloadKg > 0 ? `${payloadKg.toFixed(1)} kg` : '—'} k="Payload" />
                  <Fact v={battery ? `${battery.have}%` : '—'} k="Battery now" />
                  <Fact v={estimate ? `~${estimate.batteryPercent}%` : '—'} k="Battery needed" />
                </div>

                {!estimate && (
                  <div className="dlv-warn dim">
                    <Icon name="gps" size={14} />
                    <span>No position from the selected drone yet, so distance and flight time are unknown. Pick a connected drone to see the real numbers.</span>
                  </div>
                )}
                {estimate && battery && !battery.ok && (
                  <div className={`dlv-warn ${battery.tight ? 'amber' : 'red'}`}>
                    <Icon name="altitude" size={14} />
                    <span>
                      {battery.tight
                        ? `Tight: about ${estimate.batteryPercent}% needed and ${battery.have}% on board leaves no reserve. Plan for ${battery.need}%.`
                        : `Not enough battery: this run needs about ${estimate.batteryPercent}% plus reserve (${battery.need}% total), the drone has ${battery.have}%.`}
                    </span>
                  </div>
                )}
                {estimate && !battery && (
                  <div className="dlv-warn dim">
                    <Icon name="altitude" size={14} />
                    <span>Drone is not reporting a battery level, so the run is not checked against charge.</span>
                  </div>
                )}

                <div className="dlv-scroll">
                  <table className="dlv-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Stop</th>
                        <th>Recipient</th>
                        <th>Weight</th>
                        <th style={{ width: 96 }}>Order</th>
                        <th style={{ width: 40 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {selectedJobs.map((job, i) => (
                        <tr key={job._id}>
                          <td><span className="dlv-ord">{i + 1}</span></td>
                          <td style={{ fontWeight: 600 }}>{locationName(job)}</td>
                          <td>{job.recipient || '—'}</td>
                          <td>{job.parcelWeightKg ? `${job.parcelWeightKg} kg` : '—'}</td>
                          <td>
                            <button className="dlv-btn tiny" title="Move earlier" disabled={i === 0} onClick={() => moveStop(job._id, -1)}>↑</button>
                            {' '}
                            <button className="dlv-btn tiny" title="Move later" disabled={i === selectedJobs.length - 1} onClick={() => moveStop(job._id, 1)}>↓</button>
                          </td>
                          <td>
                            <button className="dlv-btn link" title="Remove from this run" onClick={() => toggleJob(job._id)}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="dlv-bar">
                  <select
                    className="dlv-in"
                    style={{ width: 220 }}
                    value={selectedDroneId || ''}
                    onChange={(e) => onDroneSelect && onDroneSelect(e.target.value || null)}
                  >
                    <option value="">Select drone…</option>
                    {drones.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name || d.id}{d.status === 'connected' ? '' : ' (offline)'}
                      </option>
                    ))}
                  </select>

                  {selectedHasMedical ? (
                    <span className="dlv-est" style={{ color: '#fb923c' }}>
                      {selectedJobs.length === 1 ? 'Medical: lands on the pad and waits' : 'A medical delivery flies alone — select only it'}
                    </span>
                  ) : (
                  <select
                    className="dlv-in"
                    style={{ width: 170 }}
                    value={dropMethod}
                    onChange={(e) => setDropMethod(e.target.value)}
                    title="Winch: hover and lower the parcel on rope. Touch-drop: descend until the parcel touches the ground, release the hook, climb away."
                  >
                    <option value="winch">Winch drop</option>
                    <option value="touch">Touch-drop</option>
                  </select>
                  )}

                  <button
                    className="dlv-btn primary"
                    onClick={dispatch}
                    disabled={busy || selectedJobIds.length === 0 || !droneConnected}
                  >
                    <Icon name="takeoffAction" size={14} />
                    Dispatch {selectedJobIds.length} {selectedJobIds.length === 1 ? 'stop' : 'stops'}
                  </button>

                  <button className="dlv-btn" onClick={() => setSelectedJobIds([])} disabled={busy}>Clear</button>

                  {selectedJobIds.length >= MAX_STOPS && (
                    <span className="dlv-est">Max {MAX_STOPS} stops per mission</span>
                  )}
                  {selectedDroneId && !droneConnected && (
                    <span className="dlv-est" style={{ color: '#fbbf24' }}>Drone not connected</span>
                  )}
                  {!selectedDroneId && <span className="dlv-est">Pick a drone to dispatch</span>}
                  {hardwareMissing && !selectedHasMedical && (
                    <span className="dlv-est" style={{ color: '#fbbf24' }}>
                      No {dropMethod === 'touch' ? 'release hook' : 'winch'} recorded for this drone — set it in Fleet
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ── Where the last mission went ── */}
            {lastDispatch && (
              <div className="dlv-card">
                <h2><Icon name="missions" size={14} /> Last dispatch</h2>
                <p className="dlv-est" style={{ margin: '0 0 12px', lineHeight: 1.6 }}>
                  <b>{lastDispatch.stops} {lastDispatch.stops === 1 ? 'stop' : 'stops'}</b> uploaded to <b>{lastDispatch.droneId}</b>
                  {lastDispatch.mission?.name ? <> as <b>{lastDispatch.mission.name}</b></> : null}.
                  {' '}It is on the vehicle but not flying — start it from Missions, where the preflight checklist runs.
                </p>
                {lastDispatch.deconfliction && (
                  <p className="dlv-est" style={{ margin: '0 0 12px', lineHeight: 1.6 }}>
                    {lastDispatch.deconfliction.adjusted ? lastDispatch.deconfliction.summary : lastDispatch.deconfliction.message}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {onNavigate && (
                    <button className="dlv-btn primary" onClick={() => onNavigate(SECTIONS.MISSIONS)}>
                      <Icon name="missions" size={14} /> Open in Missions
                    </button>
                  )}
                  <button className="dlv-btn" onClick={() => setLastDispatch(null)}>Dismiss</button>
                </div>
              </div>
            )}

            {/* ── Top Row: Queue a parcel & Active side-by-side ── */}
            <div className="dlv-top-grid">
              {/* ── New parcel ── */}
              <div className="dlv-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                <h2><Icon name="package" size={14} /> Queue a parcel</h2>
                {locations.length === 0 ? (
                  <div className="dlv-empty" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <b>No saved locations yet</b>
                    A parcel needs somewhere to go.
                    <div style={{ marginTop: 12 }}>
                      <button className="dlv-btn primary" onClick={() => setTab('locations')}>Add a location</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                      <div>
                        <label className="dlv-lbl">Location</label>
                        <select
                          className="dlv-in"
                          value={jobForm.locationId}
                          onChange={(e) => setJobForm({ ...jobForm, locationId: e.target.value })}
                        >
                          <option value="">Select…</option>
                          {locations.filter((l) => l.active).map((l) => (
                            <option key={l._id} value={l._id}>{l.name}{l.landingPad ? ' (pad)' : ''}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="dlv-lbl">Type</label>
                        <select
                          className="dlv-in"
                          value={jobForm.type}
                          onChange={(e) => setJobForm({ ...jobForm, type: e.target.value })}
                          title="Ecom: the drone drops the parcel and flies on. Medical: it lands on the pad, waits for the recipient, then you send it back."
                        >
                          <option value="ecom">Ecom — drop and fly on</option>
                          <option
                            value="medical"
                            disabled={!locations.find((l) => l._id === jobForm.locationId)?.landingPad}
                          >
                            Medical — land, hand over, return{locations.find((l) => l._id === jobForm.locationId)?.landingPad ? '' : ' (needs a landing pad)'}
                          </option>
                        </select>
                      </div>
                      <div>
                        <label className="dlv-lbl">Recipient (Optional)</label>
                        <input
                          className="dlv-in"
                          value={jobForm.recipient}
                          onChange={(e) => setJobForm({ ...jobForm, recipient: e.target.value })}
                          placeholder="Optional"
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <label className="dlv-lbl">Weight (kg)</label>
                          <input
                            className="dlv-in"
                            type="number"
                            min="0"
                            step="0.1"
                            value={jobForm.parcelWeightKg}
                            onChange={(e) => setJobForm({ ...jobForm, parcelWeightKg: e.target.value })}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="dlv-lbl">Note</label>
                          <input
                            className="dlv-in"
                            value={jobForm.note}
                            onChange={(e) => setJobForm({ ...jobForm, note: e.target.value })}
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="dlv-bar" style={{ marginTop: 14, paddingTop: 14 }}>
                      <button className="dlv-btn primary" style={{ width: '100%', justifyContent: 'center' }} onClick={addJob} disabled={busy || !jobForm.locationId}>
                        Add to queue
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* ── Active ── */}
              <div className="dlv-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 14, gap: 12 }}>
                  <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    <span>Active · <span className="count">{liveCount}</span></span>
                  </h2>
                  {liveCount > 0 && (
                    <button
                      type="button"
                      className="dlv-btn link"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'auto',
                        minWidth: 0,
                        maxWidth: 'fit-content',
                        height: 22,
                        padding: '0 10px',
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        border: '1px solid color-mix(in srgb, var(--accentSecondary) 40%, transparent)',
                        borderRadius: 4,
                        color: 'var(--accentPrimary, var(--accentSecondary))',
                        background: 'color-mix(in srgb, var(--accentSecondary) 8%, transparent)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexGrow: 0,
                        flexShrink: 0,
                        marginLeft: 'auto',
                      }}
                      onClick={reconcileStaleJobs}
                      disabled={busy}
                      title="Automatically resolve deliveries whose drone flights have ended"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {liveCount === 0 ? (
                  <div className="dlv-empty" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <b>Nothing in the air</b>Dispatched parcels appear here and update themselves.
                  </div>
                ) : (
                  <div className="dlv-scroll" style={{ flex: 1 }}>
                    <table className="dlv-tbl">
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Recipient</th>
                          <th>Drone</th>
                          <th>Mission</th>
                          <th>Status</th>
                          <th style={{ width: 170 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {[...byStatus.assigned, ...byStatus.in_flight, ...medicalParked].map((job) => (
                          <tr key={job._id}>
                            <td style={{ fontWeight: 600 }}>
                              {locationName(job)}
                              {job.type === 'medical' && <span className="dlv-est" style={{ marginLeft: 6, color: '#fb923c' }}>MEDICAL</span>}
                            </td>
                            <td>{job.recipient || '—'}</td>
                            <td>{job.droneId || '—'}</td>
                            <td style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)' }}>{job.returnMissionId || job.missionId || '—'}</td>
                            <td>
                              <StatusChip status={job.status} />
                              {job.type === 'medical' && job.status === 'landed' && waitedMin(job) != null && (
                                <div
                                  className="dlv-est"
                                  style={{ marginTop: 4, color: waitedMin(job) >= pickupTimeoutMin ? '#f87171' : undefined, fontWeight: waitedMin(job) >= pickupTimeoutMin ? 700 : undefined }}
                                  title={`Alert after ${pickupTimeoutMin} min (Settings → Delivery)`}
                                >
                                  Waiting {waitedMin(job)} min{waitedMin(job) >= pickupTimeoutMin ? ' — call the recipient' : ''}
                                </div>
                              )}
                              {job.type === 'medical' && job.status === 'delivered' && (
                                <div className="dlv-est" style={{ marginTop: 4 }}>
                                  Received{job.receivedBy ? ` (${job.receivedBy})` : ''} · {job.returnMissionId ? 'return uploaded — Start in Missions' : 'on the pad, ready to return'}
                                </div>
                              )}
                            </td>
                            <td>
                              {job.type === 'medical' && (job.status === 'landed' || job.status === 'delivered') ? (
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                                  {job.status === 'landed' && job.trackToken && (
                                    <button className="dlv-btn link" onClick={() => copyRecipientLink(job)} disabled={busy} title="Link the recipient opens to confirm they took the parcel">
                                      Recipient link
                                    </button>
                                  )}
                                  {job.status === 'landed' && (
                                    <button className="dlv-btn link" style={{ color: '#34d399' }} onClick={() => markReceived(job)} disabled={busy} title="The recipient confirmed by phone that they took the parcel and are clear of the drone">
                                      Received
                                    </button>
                                  )}
                                  <button className="dlv-btn primary tiny" onClick={() => sendDroneBack(job)} disabled={busy} title="Confirm the parcel was taken (if not yet), upload the flight back to where it took off, and start it after preflight">
                                    Send drone back
                                  </button>
                                  <button className="dlv-btn link" onClick={() => markRecovered(job)} disabled={busy} title="The drone is back at base by other means — frees it and the pad">
                                    Recovered
                                  </button>
                                </div>
                              ) : (
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                                <button
                                  className="dlv-btn link"
                                  onClick={() => requeueJob(job._id)}
                                  disabled={busy}
                                  title="Requeue parcel into pending list"
                                >
                                  Requeue
                                </button>
                                {job.type !== 'medical' && (
                                <button
                                  className="dlv-btn link"
                                  style={{ color: 'var(--accentPrimary, var(--accentSecondary))' }}
                                  onClick={() => completeJob(job._id)}
                                  disabled={busy}
                                  title="Mark this parcel as delivered"
                                >
                                  Delivered
                                </button>
                                )}
                                <button
                                  className="dlv-btn link"
                                  style={{ color: 'var(--danger)' }}
                                  onClick={() => deleteJob(job._id)}
                                  disabled={busy}
                                  title="Delete delivery record"
                                >
                                  <Icon name="disconnect" size={13} />
                                </button>
                              </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* ── Pending ── */}
            <div className="dlv-card">
              <h2>Pending · <span className="count">{byStatus.pending.length}</span></h2>
              {byStatus.pending.length === 0 ? (
                <div className="dlv-empty">
                  <b>Nothing waiting to fly</b>
                  Queue a parcel above and it lands here.
                </div>
              ) : (
                <div className="dlv-scroll">
                  <table className="dlv-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 34 }} />
                        <th style={{ width: 40 }}>#</th>
                        <th>Location</th>
                        <th>Recipient</th>
                        <th>Weight</th>
                        <th>Note</th>
                        <th style={{ width: 60 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {byStatus.pending.map((job) => {
                        const order = selectedJobIds.indexOf(job._id);
                        const checked = order >= 0;
                        return (
                          <tr key={job._id} className={checked ? 'sel' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!checked && selectedJobIds.length >= MAX_STOPS}
                                onChange={() => toggleJob(job._id)}
                              />
                            </td>
                            <td>{checked ? <span className="dlv-ord">{order + 1}</span> : ''}</td>
                            <td style={{ fontWeight: 600 }}>
                              {locationName(job)}
                              {job.type === 'medical' && <span className="dlv-est" style={{ marginLeft: 6, color: '#fb923c' }}>MEDICAL</span>}
                            </td>
                            <td>{job.recipient || '—'}</td>
                            <td>{job.parcelWeightKg ? `${job.parcelWeightKg} kg` : '—'}</td>
                            <td style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)' }}>{job.note || '—'}</td>
                            <td>
                              <button className="dlv-btn link" title="Remove" onClick={() => deleteJob(job._id)} disabled={busy}>
                                <Icon name="disconnect" size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── Finished ── */}
            <div className="dlv-card">
              <h2>Finished · <span className="count">{finished.length}</span></h2>
              {finished.length === 0 ? (
                <div className="dlv-empty"><b>No completed deliveries yet</b>History shows up here once a parcel lands.</div>
              ) : (
                <>
                  <div className="dlv-scroll">
                    <table className="dlv-tbl">
                      <thead>
                        <tr>
                          <th>Location</th>
                          <th>Recipient</th>
                          <th>Drone</th>
                          <th>Status</th>
                          <th>Updated</th>
                          <th style={{ width: 130 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {finishedShown.map((job) => (
                          <tr key={job._id}>
                            <td style={{ fontWeight: 600 }}>{locationName(job)}</td>
                            <td>{job.recipient || '—'}</td>
                            <td>{job.droneId || '—'}</td>
                            <td><StatusChip status={job.status} /></td>
                            <td style={{ color: 'var(--textMuted)' }}>
                              {job.updatedAt ? new Date(job.updatedAt).toLocaleString() : '—'}
                            </td>
                            <td>
                              {job.status === 'failed' && (
                                <button className="dlv-btn link" onClick={() => requeueJob(job._id)} disabled={busy}>
                                  Requeue
                                </button>
                              )}
                              <button className="dlv-btn link" onClick={() => deleteJob(job._id)} disabled={busy}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {finished.length > FINISHED_PAGE && (
                    <div className="dlv-bar">
                      <button className="dlv-btn" onClick={() => setShowAllFinished((v) => !v)}>
                        {showAllFinished ? 'Show recent only' : `Show all ${finished.length}`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        ) : tab === 'requests' ? (
          <>
            {/* ── Customer drop requests & re-requests awaiting approval ── */}
            <div className="dlv-card">
              {/* Left-aligned underline tabs attached to the table border */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                borderBottom: '1px solid var(--borderSecondary)',
                marginBottom: 12,
                width: '100%',
              }}>
                <button
                  type="button"
                  onClick={() => setRequestSubTab('requests')}
                  style={{
                    width: 'auto',
                    flex: '0 0 auto',
                    background: 'none',
                    border: 'none',
                    borderBottom: requestSubTab === 'requests' ? '2px solid var(--accent, #00f0ff)' : '2px solid transparent',
                    borderRadius: 0,
                    margin: 0,
                    marginBottom: -1,
                    padding: '8px 4px 10px 0',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    fontSize: 12.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: requestSubTab === 'requests' ? '#ffffff' : 'color-mix(in srgb, var(--textPrimary) 55%, transparent)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    transform: 'none',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Icon name="package" size={14} />
                  <span>Customer requests</span>
                  <span style={{
                    color: requestSubTab === 'requests' ? 'var(--accent, #00f0ff)' : 'color-mix(in srgb, var(--textPrimary) 45%, transparent)',
                    fontWeight: 700,
                  }}>
                    · {customerRequests.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setRequestSubTab('rerequests')}
                  style={{
                    width: 'auto',
                    flex: '0 0 auto',
                    background: 'none',
                    border: 'none',
                    borderBottom: requestSubTab === 'rerequests' ? '2px solid var(--accent, #00f0ff)' : '2px solid transparent',
                    borderRadius: 0,
                    margin: 0,
                    marginBottom: -1,
                    padding: '8px 4px 10px 4px',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    fontSize: 12.5,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: requestSubTab === 'rerequests' ? '#ffffff' : 'color-mix(in srgb, var(--textPrimary) 55%, transparent)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    transform: 'none',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Icon name="package" size={14} />
                  <span>Customer re-requests</span>
                  <span style={{
                    color: requestSubTab === 'rerequests' ? 'var(--accent, #00f0ff)' : 'color-mix(in srgb, var(--textPrimary) 45%, transparent)',
                    fontWeight: 700,
                  }}>
                    · {customerReRequests.length}
                  </span>
                </button>
              </div>

              {currentRequestsList.length === 0 ? (
                <div className="dlv-empty" style={{ padding: '24px 12px' }}>
                  <b>{requestSubTab === 'requests' ? 'No customer requests waiting' : 'No customer re-requests waiting'}</b>
                  <span>
                    {requestSubTab === 'requests'
                      ? 'Share a drop link below and customer requests land here for approval.'
                      : 'Customer re-requests awaiting review will appear here.'}
                  </span>
                </div>
              ) : requestSubTab === 'rerequests' ? (
                /* Dedicated Customer Re-requests Table */
                <div className="dlv-scroll">
                  <table className="dlv-tbl">
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th>Contact</th>
                        <th>Reason</th>
                        <th>Date &amp; Time</th>
                        <th style={{ width: 170, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerReRequests.map((job) => (
                        <tr key={job._id}>
                          <td>
                            <div style={{ fontWeight: 600, color: '#ffffff' }}>
                              {job.label || job.recipient || '—'}
                            </div>
                            {job.recipient && job.recipient !== job.label && (
                              <div style={{ fontSize: 11, color: 'color-mix(in srgb, var(--textPrimary) 65%, transparent)', marginTop: 2 }}>
                                {job.recipient}
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ color: 'color-mix(in srgb, var(--textPrimary) 90%, transparent)' }}>
                              {job.contact || '—'}
                            </div>
                          </td>
                          <td>
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '3px 8px',
                              borderRadius: 6,
                              background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
                              border: '1px solid color-mix(in srgb, var(--warning) 35%, transparent)',
                              color: '#fbbf24',
                              fontSize: 11.5,
                              fontWeight: 600,
                            }}>
                              <span>🔄</span>
                              <span>{job.reRequestReason || 'Location sharing re-request'}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ color: '#ffffff', fontSize: 12 }}>
                              {job.reRequestedAt
                                ? new Date(job.reRequestedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
                                : (job.updatedAt ? new Date(job.updatedAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—')}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                              {job.reRequestStatus === 'accepted' ? (
                                <button
                                  type="button"
                                  className="dlv-btn"
                                  onClick={() => copyReRequestLink(job)}
                                  style={{
                                    height: 28,
                                    padding: '0 12px',
                                    borderRadius: 14,
                                    background: 'rgba(0, 240, 255, 0.15)',
                                    border: '1px solid var(--accent, #00f0ff)',
                                    color: 'var(--accent, #00f0ff)',
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 5,
                                  }}
                                  title="Copy link to send to customer"
                                >
                                  <Icon name={copiedJobId === job._id ? 'check' : 'link'} size={13} />
                                  <span>{copiedJobId === job._id ? 'Copied' : 'Copy Link'}</span>
                                </button>
                              ) : job.reRequestStatus === 'rejected' ? (
                                <span style={{
                                  padding: '3px 8px',
                                  borderRadius: 6,
                                  background: 'color-mix(in srgb, var(--danger) 15%, transparent)',
                                  border: '1px solid color-mix(in srgb, var(--danger) 35%, transparent)',
                                  color: '#f87171',
                                  fontSize: 11,
                                  fontWeight: 700,
                                }}>
                                  Rejected
                                </span>
                              ) : (
                                <>
                                  <button
                                    className="dlv-btn link"
                                    onClick={() => acceptReRequest(job._id)}
                                    disabled={busy}
                                    style={{ color: '#34d399', fontWeight: 700 }}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    className="dlv-btn link danger"
                                    onClick={() => rejectReRequest(job)}
                                    disabled={busy}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Customer Requests Table */
                <div className="dlv-scroll">
                  <table className="dlv-tbl">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Drop Point &amp; Location</th>
                        <th>Date &amp; Time</th>
                        <th>Note</th>
                        <th style={{ width: 170 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {customerRequests.map((job) => (
                        <tr key={job._id}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{job.recipient || '—'}</div>
                            {job.contact && <div style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)', fontSize: 11, marginTop: 2 }}>{job.contact}</div>}
                          </td>
                          <td>
                            {job.location ? (
                              <div>
                                <a
                                  href={`https://www.google.com/maps?q=${job.location.lat},${job.location.lon}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="View on Google Maps"
                                  style={{
                                    color: '#00f0ff',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    fontFamily: 'monospace',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                  }}
                                >
                                  {job.location.lat.toFixed(5)}, {job.location.lon.toFixed(5)}
                                  <span style={{ fontSize: 10, opacity: 0.8 }}>↗</span>
                                </a>
                                {job.location.address && (
                                  <div style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)', fontSize: 11, marginTop: 2 }}>{job.location.address}</div>
                                )}
                                {Number.isFinite(job.location.groundElevM) && (
                                  <div style={{ color: 'color-mix(in srgb, var(--textPrimary) 70%, transparent)', fontSize: 10, marginTop: 1 }}>
                                    ground {Math.round(job.location.groundElevM)} m MSL
                                  </div>
                                )}
                              </div>
                            ) : (
                              '(location deleted)'
                            )}
                          </td>
                          <td>
                            <div style={{ color: '#ffffff' }}>
                              {job.createdAt ? new Date(job.createdAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                            </div>
                            {job.preferredAt && (
                              <div style={{ color: 'var(--accent, #00f0ff)', fontSize: 11, marginTop: 2 }}>
                                Wanted: {new Date(job.preferredAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                              </div>
                            )}
                          </td>
                          <td style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)', maxWidth: 220 }}>
                            <div>{job.note || '—'}</div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                              <button className="dlv-btn link" onClick={() => approveJob(job._id)} disabled={busy || !job.location}>
                                Approve
                              </button>
                              <button className="dlv-btn link danger" onClick={() => rejectJob(job)} disabled={busy}>
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Automatic checks that did not block the request but the operator
                  should see before approving a flight to that point. */}
              {currentRequestsList.some((j) => (j.siteWarnings || []).length > 0) && (
                <div style={{ marginTop: 14 }}>
                  {currentRequestsList.filter((j) => (j.siteWarnings || []).length > 0).map((job) => (
                    <div className="dlv-warn amber" key={`w-${job._id}`}>
                      <span>
                        <b>{job.recipient || 'Request'}:</b> {job.siteWarnings.join(' · ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Share links ── */}
            <div className="dlv-card">
              <h2>
                <span>Customer drop links · <span className="count">{links.length}</span></span>
                {links.some((l) => !l.active || (l.expiresAt && new Date(l.expiresAt) < new Date())) && (
                  <button
                    type="button"
                    className="dlv-btn link"
                    style={{
                      marginLeft: 'auto',
                      height: 24,
                      padding: '0 8px',
                      fontSize: 10,
                      fontWeight: 600,
                      border: '1px solid var(--borderSecondary)',
                      borderRadius: 4,
                      color: 'var(--danger)',
                      background: 'color-mix(in srgb, var(--danger) 8%, transparent)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={clearInactiveLinks}
                    disabled={busy}
                    title="Clear all expired and revoked links"
                  >
                    Clear Inactive
                  </button>
                )}
              </h2>
              <div className="dlv-grid" style={{ gridTemplateColumns: '1.3fr 1.3fr 1.2fr 0.8fr auto' }}>
                <div>
                  <label className="dlv-lbl">Label</label>
                  <input className="dlv-in" value={linkForm.label} placeholder="e.g. Wardha Sector 4"
                    onChange={(e) => setLinkForm({ ...linkForm, label: e.target.value })} />
                </div>
                <div>
                  <label className="dlv-lbl">Delivery Center / Hub</label>
                  <select className="dlv-in" value={linkForm.centerId}
                    onChange={(e) => setLinkForm({ ...linkForm, centerId: e.target.value })}>
                    <option value="">Default Hub / Base</option>
                    {centers.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} ({((c.serviceRadiusMeters || 6000) / 1000).toFixed(1)} km)
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="dlv-lbl">Contact</label>
                  <input className="dlv-in" value={linkForm.contact || ''} placeholder="e.g. +91 98765 43210"
                    onChange={(e) => setLinkForm({ ...linkForm, contact: e.target.value })} />
                </div>
                <div>
                  <label className="dlv-lbl">Expires in (days)</label>
                  <input className="dlv-in" inputMode="numeric" value={linkForm.expiresInDays}
                    onChange={(e) => setLinkForm({ ...linkForm, expiresInDays: e.target.value })} />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button className="dlv-btn primary" onClick={createLink} disabled={busy} style={{ width: 'auto', minWidth: 84 }}>
                    <Icon name="send" size={13} /> Send
                  </button>
                </div>
              </div>

              {links.length > 0 && (
                <div className="dlv-scroll" style={{ marginTop: 14 }}>
                  <table className="dlv-tbl">
                    <thead>
                      <tr>
                        <th>Label</th>
                        <th>Delivery Hub</th>
                        <th>Contact</th>
                        <th style={{ width: 80, textAlign: 'center' }}>Requests</th>
                        <th>Expires In</th>
                        <th>Status</th>
                        <th style={{ width: 170, textAlign: 'center' }}>Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {links.map((link) => (
                        <tr key={link._id}>
                          <td style={{ fontWeight: 600 }}>{link.label || '(unlabelled)'}</td>
                          <td>
                            {link.centerId?.name ? (
                              <div>
                                <span style={{ fontWeight: 600, color: 'var(--accent, #00f0ff)' }}>{link.centerId.name}</span>
                                <span style={{ display: 'block', fontSize: 10, color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)' }}>
                                  radius {((link.centerId.serviceRadiusMeters || 6000) / 1000).toFixed(1)} km
                                </span>
                              </div>
                            ) : (
                              <span style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)' }}>Default Depot</span>
                            )}
                          </td>
                          <td>
                            <span style={{ color: link.contact ? '#ffffff' : 'color-mix(in srgb, var(--textPrimary) 45%, transparent)', fontFamily: link.contact ? 'monospace' : 'inherit' }}>
                              {link.contact || '—'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--accent, #00f0ff)' }}>
                            {link.requestCount || 0}
                          </td>
                          <td style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)' }}>
                            {link.expiresAt ? new Date(link.expiresAt).toLocaleDateString() : 'Never'}
                          </td>
                          <td>
                            {link.active && (!link.expiresAt || new Date(link.expiresAt) > new Date())
                              ? <StatusChip status="pending" />
                              : <StatusChip status="failed" />}
                          </td>
                          <td style={{ position: 'relative', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                              {/* Clean Copy Link pill button */}
                              <button
                                type="button"
                                onClick={() => copyLink(link)}
                                style={{
                                  height: 28,
                                  padding: '0 10px',
                                  borderRadius: 6,
                                  border: '1px solid',
                                  borderColor: copiedLinkId === link._id ? '#34d399' : 'var(--borderSecondary)',
                                  background: copiedLinkId === link._id ? 'rgba(52, 211, 153, 0.15)' : 'var(--bgTertiary)',
                                  color: copiedLinkId === link._id ? '#34d399' : '#ffffff',
                                  cursor: 'pointer',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  letterSpacing: '0.04em',
                                  textTransform: 'uppercase',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  whiteSpace: 'nowrap',
                                  width: 'auto',
                                  flex: '0 0 auto',
                                  transform: 'none',
                                  boxShadow: 'none',
                                  transition: 'all 0.15s ease',
                                }}
                                title="Copy drop link to clipboard"
                              >
                                <Icon name={copiedLinkId === link._id ? 'check' : 'copy'} size={12} color={copiedLinkId === link._id ? '#34d399' : 'currentColor'} />
                                <span>{copiedLinkId === link._id ? 'Copied!' : 'Copy Link'}</span>
                              </button>

                              {/* 3-Dots Menu */}
                              <div className="dlv-menu-wrap" style={{ position: 'relative' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuLinkId((curr) => (curr === link._id ? null : link._id));
                                  }}
                                  style={{
                                    height: 28,
                                    width: 28,
                                    padding: 0,
                                    borderRadius: 6,
                                    border: '1px solid',
                                    borderColor: openMenuLinkId === link._id ? 'var(--accent, #00f0ff)' : 'var(--borderSecondary)',
                                    background: openMenuLinkId === link._id ? 'rgba(0, 240, 255, 0.1)' : 'var(--bgTertiary)',
                                    color: openMenuLinkId === link._id ? 'var(--accent, #00f0ff)' : '#ffffff',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flex: '0 0 auto',
                                    transform: 'none',
                                    boxShadow: 'none',
                                    transition: 'all 0.15s ease',
                                  }}
                                  title="Actions"
                                >
                                  <Icon name="moreVertical" size={13} />
                                </button>

                                {/* Dropdown Popover */}
                                {openMenuLinkId === link._id && (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      right: 0,
                                      top: 'calc(100% + 5px)',
                                      background: 'var(--bgSecondary, #13171d)',
                                      border: '1px solid var(--borderSecondary, #272f3d)',
                                      borderRadius: 8,
                                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.55)',
                                      zIndex: 200,
                                      minWidth: 140,
                                      padding: '4px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 2,
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {link.active && (!link.expiresAt || new Date(link.expiresAt) > new Date()) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setOpenMenuLinkId(null);
                                          revokeLink(link);
                                        }}
                                        disabled={busy}
                                        style={{
                                          width: '100%',
                                          padding: '7px 10px',
                                          background: 'none',
                                          border: 'none',
                                          borderRadius: 5,
                                          color: 'var(--warning)',
                                          fontSize: 12,
                                          fontWeight: 600,
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          cursor: 'pointer',
                                          margin: 0,
                                          transform: 'none',
                                          boxShadow: 'none',
                                          transition: 'background 0.12s ease',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--warning) 14%, transparent)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                      >
                                        <Icon name="slash" size={12} color="var(--warning)" />
                                        <span>Revoke</span>
                                      </button>
                                    )}

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setOpenMenuLinkId(null);
                                        deleteLink(link);
                                      }}
                                      disabled={busy}
                                      style={{
                                        width: '100%',
                                        padding: '7px 10px',
                                        background: 'none',
                                        border: 'none',
                                        borderRadius: 5,
                                        color: 'var(--danger)',
                                        fontSize: 12,
                                        fontWeight: 600,
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        cursor: 'pointer',
                                        margin: 0,
                                        transform: 'none',
                                        boxShadow: 'none',
                                        transition: 'background 0.12s ease',
                                      }}
                                      onMouseEnter={(e) => { e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) 14%, transparent)'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                    >
                                      <Icon name="trash" size={12} color="var(--danger)" />
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="dlv-warn dim" style={{ marginTop: 12 }}>
                <span>
                  Anyone with the URL can submit a drop point until the link expires or is revoked.
                  Every request is checked against the service area, restricted zones and aerodromes
                  before it reaches this queue — and nothing flies until you approve it.
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* ── Top row: Add location (50%) & Add center (50%) side by side ── */}
            <div className="dlv-half-grid">
              {/* ── Add / edit location ── */}
              <div className="dlv-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                <h2><Icon name="gps" size={14} /> {editingId ? 'Edit location' : 'Add location'}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                  <div>
                    <label className="dlv-lbl">Customer Name</label>
                    <input
                      className="dlv-in"
                      value={locForm.name}
                      onChange={(e) => setLocForm({ ...locForm, name: e.target.value })}
                      placeholder="e.g. Rahul Sharma"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label className="dlv-lbl">Latitude</label>
                      <input
                        className={`dlv-in${latBad ? ' bad' : ''}`}
                        inputMode="decimal"
                        value={locForm.lat}
                        onChange={(e) => setLocForm({ ...locForm, lat: e.target.value })}
                        onPaste={pasteCoords}
                        placeholder="21.14580"
                      />
                      {latBad && <span className="dlv-hint bad">Must be between -90 and 90</span>}
                    </div>
                    <div>
                      <label className="dlv-lbl">Longitude</label>
                      <input
                        className={`dlv-in${lonBad ? ' bad' : ''}`}
                        inputMode="decimal"
                        value={locForm.lon}
                        onChange={(e) => setLocForm({ ...locForm, lon: e.target.value })}
                        onPaste={pasteCoords}
                        placeholder="79.08821"
                      />
                      {lonBad && <span className="dlv-hint bad">Must be between -180 and 180</span>}
                    </div>
                  </div>
                  <div>
                    <label className="dlv-lbl">Address / Landmark</label>
                    <input
                      className="dlv-in"
                      value={locForm.address}
                      onChange={(e) => setLocForm({ ...locForm, address: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.4, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(locForm.landingPad)}
                      onChange={(e) => setLocForm({ ...locForm, landingPad: e.target.checked })}
                    />
                    <span>
                      <b>Surveyed landing pad.</b> A clear, marked spot at exactly these coordinates where a medical
                      drone may land and disarm. Only pads take medical deliveries.
                    </span>
                  </label>
                </div>
                <div className="dlv-bar" style={{ marginTop: 14, paddingTop: 14 }}>
                  <button className="dlv-btn primary" style={{ flex: 1, justifyContent: 'center' }} onClick={submitLocation} disabled={busy || !locReady}>
                    {editingId ? 'Save changes' : 'Add location'}
                  </button>
                  {editingId && (
                    <button
                      className="dlv-btn"
                      onClick={() => { setEditingId(null); setLocForm({ name: '', lat: '', lon: '', address: '', landingPad: false }); }}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: 'color-mix(in srgb, var(--textPrimary) 55%, transparent)', lineHeight: 1.4 }}>
                  Paste <b>21.1458, 79.0882</b> into either box to fill both.
                </div>
              </div>

              {/* ── Add / edit center ── */}
              <div className="dlv-card" style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
                <h2><Icon name="missions" size={14} /> {editingCenterId ? 'Edit center' : 'Add center'}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                  <div>
                    <label className="dlv-lbl">Name</label>
                    <input
                      className="dlv-in"
                      value={centerForm.name}
                      onChange={(e) => setCenterForm({ ...centerForm, name: e.target.value })}
                      placeholder="e.g. Nagpur South Hub"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label className="dlv-lbl">ID</label>
                      <input
                        className="dlv-in"
                        value={centerForm.centerId}
                        onChange={(e) => setCenterForm({ ...centerForm, centerId: e.target.value })}
                        placeholder="e.g. HUB-01"
                      />
                    </div>
                    <div>
                      <label className="dlv-lbl">Code</label>
                      <input
                        className="dlv-in"
                        value={centerForm.code}
                        onChange={(e) => setCenterForm({ ...centerForm, code: e.target.value })}
                        placeholder="e.g. NGP-S"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label className="dlv-lbl">Latitude</label>
                      <input
                        className={`dlv-in${centerLatBad ? ' bad' : ''}`}
                        inputMode="decimal"
                        value={centerForm.lat}
                        onChange={(e) => setCenterForm({ ...centerForm, lat: e.target.value })}
                        onPaste={pasteCenterCoords}
                        placeholder="21.14580"
                      />
                      {centerLatBad && <span className="dlv-hint bad">Must be between -90 and 90</span>}
                    </div>
                    <div>
                      <label className="dlv-lbl">Longitude</label>
                      <input
                        className={`dlv-in${centerLonBad ? ' bad' : ''}`}
                        inputMode="decimal"
                        value={centerForm.lon}
                        onChange={(e) => setCenterForm({ ...centerForm, lon: e.target.value })}
                        onPaste={pasteCenterCoords}
                        placeholder="79.08821"
                      />
                      {centerLonBad && <span className="dlv-hint bad">Must be between -180 and 180</span>}
                    </div>
                  </div>
                  <div>
                    <label className="dlv-lbl">Address</label>
                    <input
                      className="dlv-in"
                      value={centerForm.address}
                      onChange={(e) => setCenterForm({ ...centerForm, address: e.target.value })}
                      placeholder="Facility / Address"
                    />
                  </div>
                </div>
                <div className="dlv-bar" style={{ marginTop: 14, paddingTop: 14 }}>
                  <button className="dlv-btn primary" style={{ flex: 1, justifyContent: 'center' }} onClick={submitCenter} disabled={busy || !centerReady}>
                    {editingCenterId ? 'Save center' : 'Add center'}
                  </button>
                  {editingCenterId && (
                    <button
                      className="dlv-btn"
                      onClick={() => {
                        setEditingCenterId(null);
                        setCenterForm({ name: '', centerId: '', code: '', lat: '', lon: '', serviceRadiusKm: '6', address: '', notes: '' });
                      }}
                      disabled={busy}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: 'color-mix(in srgb, var(--textPrimary) 55%, transparent)', lineHeight: 1.4 }}>
                  Paste <b>21.1458, 79.0882</b> into either box to fill both.
                </div>
              </div>
            </div>

            {/* ── Sub-tabbed Lists: Saved Locations & Centers ── */}
            <div className="dlv-card">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
                borderBottom: '1px solid var(--borderSecondary)',
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <button
                    type="button"
                    onClick={() => setLocSubTab('locations')}
                    style={{
                      width: 'auto',
                      flex: '0 0 auto',
                      background: 'none',
                      border: 'none',
                      borderBottom: locSubTab === 'locations' ? '2px solid var(--accent, #00f0ff)' : '2px solid transparent',
                      borderRadius: 0,
                      margin: 0,
                      marginBottom: -1,
                      padding: '8px 4px 10px 0',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      fontSize: 12.5,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: locSubTab === 'locations' ? '#ffffff' : 'color-mix(in srgb, var(--textPrimary) 55%, transparent)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      transform: 'none',
                      boxShadow: 'none',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Icon name="gps" size={14} />
                    <span>Saved locations</span>
                    <span style={{
                      color: locSubTab === 'locations' ? 'var(--accent, #00f0ff)' : 'color-mix(in srgb, var(--textPrimary) 45%, transparent)',
                      fontWeight: 700,
                    }}>
                      · {locations.length}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setLocSubTab('centers')}
                    style={{
                      width: 'auto',
                      flex: '0 0 auto',
                      background: 'none',
                      border: 'none',
                      borderBottom: locSubTab === 'centers' ? '2px solid var(--accent, #00f0ff)' : '2px solid transparent',
                      borderRadius: 0,
                      margin: 0,
                      marginBottom: -1,
                      padding: '8px 4px 10px 4px',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      fontSize: 12.5,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: locSubTab === 'centers' ? '#ffffff' : 'color-mix(in srgb, var(--textPrimary) 55%, transparent)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 7,
                      transform: 'none',
                      boxShadow: 'none',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Icon name="missions" size={14} />
                    <span>Centers</span>
                    <span style={{
                      color: locSubTab === 'centers' ? 'var(--accent, #00f0ff)' : 'color-mix(in srgb, var(--textPrimary) 45%, transparent)',
                      fontWeight: 700,
                    }}>
                      · {centers.length}
                    </span>
                  </button>
                </div>

                {locSubTab === 'locations' ? (
                  locations.length > 3 && (
                    <input
                      className="dlv-in"
                      style={{ width: 220, height: 28, textTransform: 'none' }}
                      value={locFilter}
                      onChange={(e) => setLocFilter(e.target.value)}
                      placeholder="Filter by customer or address"
                    />
                  )
                ) : (
                  centers.length > 3 && (
                    <input
                      className="dlv-in"
                      style={{ width: 220, height: 28, textTransform: 'none' }}
                      value={centerFilter}
                      onChange={(e) => setCenterFilter(e.target.value)}
                      placeholder="Filter by name, ID or address"
                    />
                  )
                )}
              </div>

              {locSubTab === 'locations' ? (
                locations.length === 0 ? (
                  <div className="dlv-empty"><b>No locations yet</b>Add the first one above.</div>
                ) : visibleLocations.length === 0 ? (
                  <div className="dlv-empty">Nothing matches “{locFilter}”.</div>
                ) : (
                  <div className="dlv-scroll">
                    <table className="dlv-tbl">
                      <thead>
                        <tr>
                          <th>Customer Name</th>
                          <th>Latitude</th>
                          <th>Longitude</th>
                          <th>Address</th>
                          <th style={{ width: 200 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {visibleLocations.map((loc) => (
                          <tr key={loc._id}>
                            <td style={{ fontWeight: 600 }}>{loc.name}</td>
                            <td>{loc.lat.toFixed(6)}</td>
                            <td>{loc.lon.toFixed(6)}</td>
                            <td style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)' }}>{loc.address || '—'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                                <button className="dlv-btn link" onClick={() => queueFor(loc)} disabled={busy}>Queue parcel</button>
                                <button className="dlv-btn link" onClick={() => startEdit(loc)} disabled={busy}>Edit</button>
                                <button className="dlv-btn link danger" onClick={() => deleteLocation(loc)} disabled={busy}>Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : (
                centers.length === 0 ? (
                  <div className="dlv-empty"><b>No delivery centers yet</b>Add the first one above.</div>
                ) : visibleCenters.length === 0 ? (
                  <div className="dlv-empty">Nothing matches “{centerFilter}”.</div>
                ) : (
                  <div className="dlv-scroll">
                    <table className="dlv-tbl">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>ID</th>
                          <th>Code</th>
                          <th>Latitude &amp; Longitude</th>
                          <th>Address</th>
                          <th style={{ width: 140 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {visibleCenters.map((c) => (
                          <tr key={c._id}>
                            <td style={{ fontWeight: 600 }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                🏢 {c.name}
                              </span>
                            </td>
                            <td style={{ fontFamily: 'monospace', color: 'var(--accent, #00f0ff)' }}>{c.centerId || '—'}</td>
                            <td style={{ fontWeight: 600 }}>{c.code || '—'}</td>
                            <td>
                              <a
                                href={`https://www.google.com/maps?q=${c.lat},${c.lon}`}
                                target="_blank"
                                rel="noreferrer"
                                title="View on Google Maps"
                                style={{
                                  color: '#00f0ff',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                  fontFamily: 'monospace',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 4,
                                }}
                              >
                                {c.lat.toFixed(5)}, {c.lon.toFixed(5)}
                                <span style={{ fontSize: 10, opacity: 0.8 }}>↗</span>
                              </a>
                            </td>
                            <td style={{ color: 'color-mix(in srgb, var(--textPrimary) 85%, transparent)' }}>{c.address || '—'}</td>
                            <td>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                                <button className="dlv-btn link" onClick={() => createLinkForCenter(c)} disabled={busy} title="Create customer drop link from this center">
                                  + Link
                                </button>
                                <button className="dlv-btn link" onClick={() => startEditCenter(c)} disabled={busy}>
                                  Edit
                                </button>
                                <button className="dlv-btn link danger" onClick={() => deleteCenter(c)} disabled={busy}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          </>
        )}
        </div>

        <aside className="dlv-sidebar" aria-label="Delivery Statistics">
          <div className="dlv-stats-vertical">
            <Stat
              n={locations.length}
              k="Location"
              onClick={() => setTab('locations')}
              active={tab === 'locations'}
            />
            <Stat
              n={byStatus.requested.length}
              k="Request"
              live={byStatus.requested.length > 0}
              onClick={() => setTab('requests')}
              active={tab === 'requests'}
            />
            <Stat
              n={byStatus.pending.length}
              k="Pending"
              onClick={() => setTab('queue')}
              active={tab === 'queue'}
            />
            <Stat
              n={byStatus.delivered.length}
              k="Delivered"
              onClick={() => setTab('queue')}
            />
            <Stat
              n={byStatus.in_flight.length + byStatus.assigned.length}
              k="In Flight"
              live={(byStatus.in_flight.length + byStatus.assigned.length) > 0}
              onClick={() => setTab('queue')}
            />
            <Stat
              n={byStatus.failed.length}
              k="Failed"
              onClick={() => setTab('queue')}
            />
          </div>
        </aside>
      </div>
    </div>
  </div>
);
}

export default DeliveriesPage;
